'use client';

import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Input,
  Select,
  Tooltip,
  Row,
  Col,
  Progress,
  Modal,
  Tag,
  Alert,
  Tabs,
  TablePaginationConfig,
} from 'antd';
import { toast } from 'react-toastify';
import {
  ShoppingOutlined,
  FileDoneOutlined,
  FieldTimeOutlined,
  BankOutlined,
  DollarOutlined,
  ReloadOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/components/AuthContext';
import { POStatus } from '@prisma/client';
import { PastelStatusBadge } from '@/components/PastelBadge';
import { PORecord } from '@/lib/mockPoStore';
import { useDebounce } from '@/hooks/useDebounce';

const { Title, Text } = Typography;

export default function PurchaseOrdersPage() {
  return (
    <Suspense fallback={null}>
      <PurchaseOrdersContent />
    </Suspense>
  );
}

function PurchaseOrdersContent() {
  const router = useRouter();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('po:create');

  // Server-side State
  const [pos, setPos] = useState<PORecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Server-side Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Search & Filter State with Debounce
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce<string>(searchQuery, 400);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Summary Metrics (aggregate across all records)
  const [summary, setSummary] = useState({
    totalOrders: 0,
    activeOrders: 0,
    waitingDelivery: 0,
    partiallyReceived: 0,
    completedOrders: 0,
    draftOrders: 0,
    totalSpend: 0,
    pendingItemsToOrder: 0,
  });

  const fetchServerData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));

      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());

      const res = await fetch(`/api/purchase-orders?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setPos(data.data || []);
        if (data.pagination) {
          setTotalCount(data.pagination.total || 0);
        }
        if (data.summary) {
          setSummary(data.summary);
        }
      } else {
        toast.error(data.error || 'Gagal memuat data Purchase Order');
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Koneksi bermasalah';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, debouncedSearchQuery]);

  // Reset page when search or status filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter]);

  // Fetch when page, pageSize, filter or debounced query updates
  useEffect(() => {
    fetchServerData();
  }, [fetchServerData]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
    }
  };

  const columns = [
    {
      title: 'No. PO',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 190,
      render: (text: string, r: PORecord) => {
        const isDraftWithoutNumber = r.status === POStatus.DRAFT && (!r.poNumber || r.poNumber === '[Draf Belum Terbit]');
        return (
          <Link
            href={`/purchase-orders/${r.id}`}
            style={{ fontWeight: 700, color: '#4F46E5', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
          >
            <ShoppingOutlined style={{ color: '#818CF8' }} />
            {isDraftWithoutNumber ? (
              <span style={{ color: '#D97706', fontWeight: 600 }}>[Draf Belum Terbit]</span>
            ) : (
              <span>{text}</span>
            )}
            {r.isRevision && (
              <Tag color="warning" style={{ fontSize: 10, padding: '0 4px', margin: 0, borderRadius: 4, fontWeight: 700 }}>
                Revisi
              </Tag>
            )}
          </Link>
        );
      },
    },
    {
      title: 'Tanggal PO',
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (dateStr: string) => (
        <Text style={{ color: '#64748B', fontSize: 13 }}>
          {new Date(dateStr).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </Text>
      ),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      width: 260,
      render: (_: unknown, r: PORecord) => (
        <div>
          <Text strong style={{ color: '#1E293B', display: 'block', fontSize: 13 }}>
            {r.supplierSnapshot?.name || 'Supplier Tidak Dikenal'}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.supplierSnapshot?.code} • {r.supplierSnapshot?.paymentTerms || 'Reguler'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status Pesanan',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: POStatus) => <PastelStatusBadge status={status} size="small" />,
    },
    {
      title: 'Status Penerimaan Barang',
      key: 'receivingStatus',
      width: 190,
      render: (_: unknown, r: PORecord) => {
        const totalOrdered = r.items?.reduce((acc, it) => acc + (it.orderedQty || 0), 0) || 0;
        const totalReceived = r.items?.reduce((acc, it) => acc + (it.receivedQty || 0), 0) || 0;

        if (r.status === POStatus.CANCELLED) {
          return <PastelStatusBadge status="CANCELLED" size="small" />;
        }
        if (r.status === POStatus.DRAFT) {
          return (
            <span style={{ color: '#94A3B8', fontSize: 11, fontStyle: 'italic' }}>
              Menunggu PO Terbit
            </span>
          );
        }
        if (r.status === POStatus.COMPLETED || (totalOrdered > 0 && totalReceived >= totalOrdered)) {
          return <PastelStatusBadge status="COMPLETED" size="small" />;
        }
        if (totalReceived > 0) {
          return <PastelStatusBadge status="PARTIALLY_RECEIVED" size="small" />;
        }
        return <PastelStatusBadge status="WAITING_DELIVERY" size="small" />;
      },
    },
    {
      title: 'Progres Kedatangan',
      key: 'receiving',
      width: 180,
      render: (_: unknown, r: PORecord) => {
        const totalOrdered = r.items?.reduce((acc, it) => acc + (it.orderedQty || 0), 0) || 0;
        const totalReceived = r.items?.reduce((acc, it) => acc + (it.receivedQty || 0), 0) || 0;
        const percent = totalOrdered > 0 ? Math.min(100, Math.round((totalReceived / totalOrdered) * 100)) : 0;

        if (r.status === POStatus.CANCELLED) {
          return <span style={{ color: '#DC2626', fontSize: 11, fontWeight: 600 }}>Dibatalkan</span>;
        }

        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
              <span style={{ color: '#64748B' }}>
                {totalReceived.toLocaleString('id-ID')} / {totalOrdered.toLocaleString('id-ID')} unit
              </span>
              <span style={{ fontWeight: 700, color: percent === 100 ? '#15803D' : '#4338CA' }}>
                {percent}%
              </span>
            </div>
            <Progress
              percent={percent}
              size="small"
              strokeColor={percent === 100 ? '#22C55E' : '#6366F1'}
              showInfo={false}
            />
          </div>
        );
      },
    },
    {
      title: 'Total Nilai PO',
      key: 'total',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, r: PORecord) => (
        <Text strong style={{ color: '#0F172A', fontSize: 13 }}>
          Rp {Number(r.total).toLocaleString('id-ID')}
        </Text>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      align: 'right' as const,
      render: (_: unknown, r: PORecord) => (
        <Link href={`/purchase-orders/${r.id}`} target="_blank" style={{ textDecoration: 'none' }}>
          <Button
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            style={{
              background: '#4F46E5',
              borderColor: '#4F46E5',
              fontWeight: 600,
              borderRadius: 6,
            }}
          >
            Detail PO ↗
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header Banner */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
              Purchase Order (PO)
            </Title>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#4F46E5',
                background: '#EEF2FF',
                border: '1px solid #C7D2FE',
                padding: '3px 10px',
                borderRadius: 20,
              }}
            >
              Monitoring Pesanan & Penerimaan Barang
            </span>
          </div>
          <Text style={{ color: '#64748B', fontSize: 13, marginTop: 4, display: 'block' }}>
            Monitoring pesanan pembelian ke supplier sekaligus penerimaan fisik barang masuk gudang.
          </Text>
        </div>

        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchServerData}
            loading={loading}
            style={{ borderRadius: 10, border: '1px solid #E2E8F0' }}
          >
            Segarkan
          </Button>

          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => router.push('/purchase-orders/consolidate')}
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                borderColor: '#4F46E5',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                fontWeight: 700,
                borderRadius: 10,
                height: 38,
              }}
            >
              Buat PO Baru
            </Button>
          )}
        </Space>
      </div>

      {/* Alert Pengingat: Item PR Disetujui Siap di-PO-kan */}
      {summary.pendingItemsToOrder > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<ShoppingOutlined style={{ color: '#4F46E5', fontSize: 18 }} />}
          style={{
            marginBottom: 24,
            borderRadius: 14,
            border: '1.5px solid #C7D2FE',
            background: 'linear-gradient(135deg, #EEF2FF 0%, #FAF5FF 100%)',
            boxShadow: '0 2px 10px rgba(99, 102, 241, 0.05)',
            padding: '12px 18px',
          }}
          title={
            <span style={{ fontWeight: 800, color: '#1E1B4B', fontSize: 14 }}>
              Item Menunggu Pembuatan PO
            </span>
          }
          description={
            <div style={{ marginTop: 4 }}>
              <span style={{ color: '#334155', fontSize: 13, lineHeight: 1.5 }}>
                Saat ini terdapat <strong>{summary.pendingItemsToOrder} item</strong> kebutuhan barang dari pengajuan PR yang sudah disetujui Owner dan siap diproses menjadi PO melalui tombol <strong>Buat PO Baru</strong> di atas.
              </span>
            </div>
          }
        />
      )}

      {/* KPI Stat Cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                TOTAL PO
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', marginTop: 2 }}>
                {summary.totalOrders}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>Semua PO</div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6366F1',
                fontSize: 18,
              }}
            >
              <ShoppingOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1.5px solid #BAE6FD',
              boxShadow: '0 4px 16px -2px rgba(3, 105, 161, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                PO AKTIF
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0369A1', marginTop: 2 }}>
                {summary.activeOrders}
              </div>
              <div style={{ fontSize: 11, color: '#0284C7', marginTop: 1 }}>Menunggu barang</div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#E0F2FE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284C7',
                fontSize: 18,
              }}
            >
              <FieldTimeOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1.5px solid #BBF7D0',
              boxShadow: '0 4px 16px -2px rgba(22, 101, 52, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                PO SELESAI
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#15803D', marginTop: 2 }}>
                {summary.completedOrders}
              </div>
              <div style={{ fontSize: 11, color: '#16A34A', marginTop: 1 }}>Diterima 100%</div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#DCFCE7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#16A34A',
                fontSize: 18,
              }}
            >
              <FileDoneOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1.5px solid #C7D2FE',
              boxShadow: '0 4px 16px -2px rgba(99, 102, 241, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                TOTAL NILAI
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#312E81', marginTop: 2, wordBreak: 'break-word' }}>
                Rp {Math.round(summary.totalSpend).toLocaleString('id-ID')}
              </div>
              <div style={{ fontSize: 11, color: '#6366F1', marginTop: 1 }}>Komitmen PO</div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4338CA',
                fontSize: 18,
              }}
            >
              <ShoppingOutlined />
            </div>
          </div>
        </Col>
      </Row>

      {/* Search & Filter Toolbar with Status Tabs */}
      <div
        style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 14,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Tabs
            activeKey={statusFilter}
            onChange={setStatusFilter}
            style={{ marginBottom: 0 }}
            items={[
              { key: 'ALL', label: `Semua (${summary.totalOrders})` },
              { key: POStatus.ISSUED, label: `Menunggu Pengiriman (${summary.waitingDelivery || 0})` },
              { key: POStatus.PARTIALLY_RECEIVED, label: `Diterima Sebagian (${summary.partiallyReceived || 0})` },
              { key: POStatus.COMPLETED, label: `Selesai (${summary.completedOrders || 0})` },
              { key: POStatus.DRAFT, label: `Draf (${summary.draftOrders || 0})` },
            ]}
          />

          <div style={{ width: 320 }}>
            <Input
              placeholder="Cari nomor PO, supplier, atau rekening..."
              prefix={<ShoppingOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8, height: 38 }}
            />
          </div>
        </div>
      </div>

      {/* Main Server-Side Orders Table */}
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 14,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
          overflow: 'hidden',
        }}
      >
        <Table
          columns={columns}
          dataSource={pos}
          rowKey="id"
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) =>
              `Menampilkan ${range[0]}-${range[1]} dari ${total} PO`,
          }}
        />
      </div>
    </div>
  );
}
